import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiRequest } from '../config/api';
import BackButton from '../components/BackButton';
import UserBadge from '../components/UserBadge';
import LoadingSpinner from '../components/LoadingSpinner';

interface SensorOrder {
  _id: string;
  state: string;
  createdAt: string;
  userid: string;
  aircraftid: {
    _id: string;
    type: string;
    identification: string;
    withSensor: boolean;
  };
  sensorid?: {
    _id: string;
    type: string;
    aircraftid: string;
    bluetoothId: string;
  } | null;
}

interface User {
  name: string;
  email: string;
  organization: string;
  userRole: string;
}

const SensorOrderDetail: React.FC = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const [order, setOrder] = useState<SensorOrder | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [creatingeSensor, setCreatingSensor] = useState(false);
  const [deletingSensor, setDeletingSensor] = useState(false);
  const [bluetoothId, setBluetoothId] = useState('');

  useEffect(() => {
    fetchOrderDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  const fetchOrderDetails = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await apiRequest(`/sensor-orders/${orderId}`);
      if (!response.ok) {
        throw new Error('Bestellung nicht gefunden');
      }
      const orderData = await response.json();
      setOrder(orderData);

      // Fetch user details using authentik userid (hash)
      const userResponse = await apiRequest(`/users/authentik/${orderData.userid}`);
      if (userResponse.ok) {
        const userData = await userResponse.json();
        setUser(userData);
      }
    } catch (err: any) {
      setError(err.message);
      console.error('Error fetching order details:', err);
    } finally {
      setLoading(false);
    }
  };

  const createAndLinkSensor = async () => {
    if (!bluetoothId.trim()) {
      alert('Bitte geben Sie die Bluetooth-ID des Sensors ein.');
      return;
    }
    setCreatingSensor(true);
    try {
      // Create sensor with aircraft type and bluetooth ID
      const sensorResponse = await apiRequest('/sensors', {
        method: 'POST',
        body: JSON.stringify({
          type: order?.aircraftid.type,
          aircraftid: order?.aircraftid._id,
          bluetoothId: bluetoothId.trim(),
        }),
      });

      if (!sensorResponse.ok) {
        throw new Error('Fehler beim Anlegen des Sensors');
      }

      const newSensor = await sensorResponse.json();

      // Update order with sensor ID
      const orderResponse = await apiRequest(`/sensor-orders/${orderId}`, {
        method: 'PUT',
        body: JSON.stringify({
          sensorid: newSensor._id,
          state: 'done'
        }),
      });

      if (!orderResponse.ok) {
        throw new Error('Fehler beim Verknüpfen des Sensors');
      }

      fetchOrderDetails();
    } catch (err: any) {
      alert('Fehler: ' + err.message);
    } finally {
      setCreatingSensor(false);
    }
  };

  const deleteAndUnlinkSensor = async () => {
    if (!order?.sensorid?._id) return;

    if (!window.confirm('Möchten Sie den Sensor wirklich löschen? Dies kann nicht rückgängig gemacht werden.')) {
      return;
    }

    setDeletingSensor(true);
    try {
      // Delete sensor
      const sensorResponse = await apiRequest(`/sensors/${order.sensorid._id}`, {
        method: 'DELETE',
      });

      if (!sensorResponse.ok) {
        throw new Error('Fehler beim Löschen des Sensors');
      }

      // Update order to remove sensor reference and set state to open
      const orderResponse = await apiRequest(`/sensor-orders/${orderId}`, {
        method: 'PUT',
        body: JSON.stringify({
          sensorid: null,
          state: 'open'
        }),
      });

      if (!orderResponse.ok) {
        throw new Error('Fehler beim Aktualisieren der Bestellung');
      }

      alert('Sensor erfolgreich gelöscht!');
      fetchOrderDetails();
    } catch (err: any) {
      alert('Fehler: ' + err.message);
    } finally {
      setDeletingSensor(false);
    }
  };

  if (loading) {
    return (
      <div className="container">
        <LoadingSpinner message="Lade Bestelldetails..." fullPage />
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mt-5">
        <div className="alert alert-danger">{error}</div>
        <button className="btn btn-secondary" onClick={() => navigate('/dashboard')}>
          Zurück zum Dashboard
        </button>
      </div>
    );
  }

  if (!order) {
    return null;
  }

  return (
    <div className="container mt-5">
      <div className="row">
        <div className="col-12">
          <div className="d-flex align-items-center justify-content-between mb-4">
            <BackButton to="/dashboard" label="Zurück zur Übersicht" />
            <UserBadge />
          </div>

          <div className="card shadow-sm">
            <div className="card-body p-4">
              <h2 className="card-title mb-4">Sensorbestellung Details</h2>

              <div className="row mb-4">
                <div className="col-md-6">
                  <h5>Bestellinformationen</h5>
                  <table className="table">
                    <tbody>
                      <tr>
                        <th>Bestelldatum:</th>
                        <td>{new Date(order.createdAt).toLocaleString('de-DE')}</td>
                      </tr>
                      <tr>
                        <th>Status:</th>
                        <td>
                          <span className={`badge ${order.state === 'open' ? 'bg-warning' : 'bg-success'}`}>
                            {order.state === 'open' ? 'Offen' : 'Erledigt'}
                          </span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div className="col-md-6">
                  <h5>Benutzer</h5>
                  <table className="table">
                    <tbody>
                      <tr>
                        <th>Name:</th>
                        <td>{user?.name || 'N/A'}</td>
                      </tr>
                      <tr>
                        <th>E-Mail:</th>
                        <td>{user?.email || 'N/A'}</td>
                      </tr>
                      <tr>
                        <th>Organisation:</th>
                        <td>{user?.organization || 'N/A'}</td>
                      </tr>
                      <tr>
                        <th>Rolle:</th>
                        <td>{user?.userRole === 'pilot' ? 'Pilot' : 'Flugschule'}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="row mb-4">
                <div className="col-12">
                  <h5>Flugobjekt</h5>
                  <table className="table">
                    <tbody>
                      <tr>
                        <th>Typ:</th>
                        <td>
                          {order.aircraftid.type === 'balloon' ? 'Ballon' :
                           order.aircraftid.type === 'glider' ? 'Segelflugzeug' :
                           order.aircraftid.type === 'plane' ? 'Motorflugzeug' : 'Hubschrauber'}
                        </td>
                      </tr>
                      <tr>
                        <th>Kennung:</th>
                        <td>{order.aircraftid.identification}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="row">
                <div className="col-12">
                  <h5>Sensor</h5>
                  {order.sensorid ? (
                    <div className="card border-success">
                      <div className="card-body">
                        <div className="d-flex justify-content-between align-items-center">
                          <div className="d-flex align-items-center">
                            <div className="me-3">
                              <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" fill="currentColor" className="bi bi-broadcast text-success" viewBox="0 0 16 16">
                                <path d="M3.05 3.05a7 7 0 0 0 0 9.9.5.5 0 0 1-.707.707 8 8 0 0 1 0-11.314.5.5 0 0 1 .707.707m2.122 2.122a4 4 0 0 0 0 5.656.5.5 0 1 1-.708.708 5 5 0 0 1 0-7.072.5.5 0 0 1 .708.708m5.656-.708a.5.5 0 0 1 .708 0 5 5 0 0 1 0 7.072.5.5 0 1 1-.708-.708 4 4 0 0 0 0-5.656.5.5 0 0 1 0-.708m2.122-2.12a.5.5 0 0 1 .707 0 8 8 0 0 1 0 11.313.5.5 0 0 1-.707-.707 7 7 0 0 0 0-9.9.5.5 0 0 1 0-.707M8 7a1 1 0 1 0 0 2 1 1 0 0 0 0-2"/>
                              </svg>
                            </div>
                            <div>
                              <h6 className="mb-1 text-success">✓ Sensor zugewiesen</h6>
                              <p className="mb-0 text-muted small">
                                <strong>ID:</strong> {order.sensorid._id}<br />
                                <strong>Typ:</strong> {order.sensorid.type === 'balloon' ? 'Ballon' :
                                 order.sensorid.type === 'glider' ? 'Segelflugzeug' :
                                 order.sensorid.type === 'plane' ? 'Motorflugzeug' : 'Hubschrauber'}<br />
                                <strong>Bluetooth-ID:</strong> {order.sensorid.bluetoothId}
                              </p>
                            </div>
                          </div>
                          <button
                            className="btn btn-outline-danger"
                            onClick={deleteAndUnlinkSensor}
                            disabled={deletingSensor}
                          >
                            {deletingSensor ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                Wird gelöscht...
                              </>
                            ) : (
                              <>
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-trash me-2" viewBox="0 0 16 16">
                                  <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5m2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5m3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0z"/>
                                  <path d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4zM2.5 3h11V2h-11z"/>
                                </svg>
                                Sensor löschen
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="card border-warning">
                      <div className="card-body text-center py-5">
                        <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" fill="currentColor" className="bi bi-broadcast-pin text-warning mb-3" viewBox="0 0 16 16">
                          <path d="M3.05 3.05a7 7 0 0 0 0 9.9.5.5 0 0 1-.707.707 8 8 0 0 1 0-11.314.5.5 0 0 1 .707.707m2.122 2.122a4 4 0 0 0 0 5.656.5.5 0 1 1-.708.708 5 5 0 0 1 0-7.072.5.5 0 0 1 .708.708m5.656-.708a.5.5 0 0 1 .708 0 5 5 0 0 1 0 7.072.5.5 0 1 1-.708-.708 4 4 0 0 0 0-5.656.5.5 0 0 1 0-.708m2.122-2.12a.5.5 0 0 1 .707 0 8 8 0 0 1 0 11.313.5.5 0 0 1-.707-.707 7 7 0 0 0 0-9.9.5.5 0 0 1 0-.707M6 8a1 1 0 1 1 2 0 1 1 0 0 1-2 0"/>
                          <path d="M9.5 8a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0"/>
                        </svg>
                        <h5 className="text-warning mb-3">Noch kein Sensor angelegt</h5>
                        <p className="text-muted mb-4">
                          Für dieses Flugobjekt wurde noch kein Sensor erstellt.<br />
                          Der Sensor wird mit dem Typ <strong>
                            {order.aircraftid.type === 'balloon' ? 'Ballon' :
                             order.aircraftid.type === 'glider' ? 'Segelflugzeug' :
                             order.aircraftid.type === 'plane' ? 'Motorflugzeug' : 'Hubschrauber'}
                          </strong> angelegt.
                        </p>
                        <div className="row justify-content-center mb-3">
                          <div className="col-md-6">
                            <label htmlFor="bluetoothId" className="form-label fw-semibold">
                              Bluetooth-ID <span className="text-danger">*</span>
                            </label>
                            <input
                              id="bluetoothId"
                              type="text"
                              className="form-control"
                              placeholder="z. B. AA:BB:CC:DD:EE:FF"
                              value={bluetoothId}
                              onChange={(e) => setBluetoothId(e.target.value)}
                              disabled={creatingeSensor}
                            />
                          </div>
                        </div>
                        <button
                          className="btn btn-success btn-lg"
                          onClick={createAndLinkSensor}
                          disabled={creatingeSensor || !bluetoothId.trim()}
                        >
                          {creatingeSensor ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                              Sensor wird angelegt...
                            </>
                          ) : (
                            <>
                              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" className="bi bi-plus-circle me-2" viewBox="0 0 16 16">
                                <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14m0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16"/>
                                <path d="M8 4a.5.5 0 0 1 .5.5v3h3a.5.5 0 0 1 0 1h-3v3a.5.5 0 0 1-1 0v-3h-3a.5.5 0 0 1 0-1h3v-3A.5.5 0 0 1 8 4"/>
                              </svg>
                              Sensor jetzt anlegen
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SensorOrderDetail;
